import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { signOut } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { supabase } from '@/lib/supabase';
import { hashPassword, verifyPassword } from '@/lib/hashUtils';


/* ─────────────────────────────────────────
   通用底部抽屉
───────────────────────────────────────── */
const Drawer = ({ open, onClose, title, children }: {
  open: boolean; onClose: () => void; title: string; children: React.ReactNode;
}) => {
  if (!open) return null;
  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={onClose} />
      <div
        className="fixed bottom-0 left-0 right-0 max-w-[480px] mx-auto bg-white rounded-t-3xl z-50"
        style={{ animation: 'slideUp 0.28s cubic-bezier(0.34,1.56,0.64,1)' }}
      >
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-gray-200 rounded-full" />
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-50">
          <h2 className="font-bold text-gray-900 text-base">{title}</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center cursor-pointer text-gray-400">
            <i className="ri-close-line text-xl" />
          </button>
        </div>
        <div className="px-5 py-4 pb-8 overflow-y-auto max-h-[80vh]">{children}</div>
      </div>
    </>
  );
};

/* ─────────────────────────────────────────
   Toast
───────────────────────────────────────── */
const Toast = ({ msg }: { msg: string }) => (
  msg ? (
    <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[100] bg-gray-900/90 text-white text-sm px-5 py-2.5 rounded-full whitespace-nowrap">
      <i className="ri-checkbox-circle-line text-emerald-400 mr-1.5" />
      {msg}
    </div>
  ) : null
);

/* ─────────────────────────────────────────
   个人信息抽屉
───────────────────────────────────────── */
const ProfileDrawer = ({ open, onClose, nickname, phone, userId, onSaved }: {
  open: boolean; onClose: () => void;
  nickname: string; phone: string; userId: string;
  onSaved: (msg: string) => void;
}) => {
  const [nick, setNick] = useState(nickname);
  const [ph, setPh] = useState(phone);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { if (open) { setNick(nickname); setPh(phone); setErr(''); } }, [open, nickname, phone]);

  const handleSave = async () => {
    if (!nick.trim()) { setErr('昵称不能为空'); return; }
    setSaving(true); setErr('');
    const { error } = await supabase.from('custom_users').update({ nickname: nick.trim(), phone: ph.trim() || null }).eq('id', userId);
    setSaving(false);
    if (error) { setErr('保存失败，请重试'); return; }
    onSaved('个人信息已更新');
    onClose();
  };

  return (
    <Drawer open={open} onClose={onClose} title="个人信息">
      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium text-gray-700 block mb-1.5">昵称</label>
          <input
            value={nick} onChange={e => { setNick(e.target.value); setErr(''); }}
            placeholder="请输入昵称" maxLength={20}
            className="w-full h-12 px-4 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400 bg-gray-50"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700 block mb-1.5">手机号</label>
          <input
            value={ph} onChange={e => setPh(e.target.value)}
            placeholder="请输入手机号" maxLength={11} type="tel"
            className="w-full h-12 px-4 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400 bg-gray-50"
          />
        </div>
        {err && <p className="text-xs text-red-500 flex items-center gap-1"><i className="ri-error-warning-line" />{err}</p>}
        <button
          onClick={handleSave} disabled={saving}
          className="w-full h-12 bg-rose-500 text-white rounded-full text-sm font-medium cursor-pointer whitespace-nowrap disabled:opacity-50 mt-2"
        >
          {saving ? '保存中...' : '保存'}
        </button>
      </div>
    </Drawer>
  );
};

/* ─────────────────────────────────────────
   地址管理抽屉
───────────────────────────────────────── */
interface Address {
  id: string; name: string; phone: string;
  province: string; city: string; district: string;
  detail: string; is_default: boolean;
}

const emptyAddr = (): Omit<Address, 'id'> => ({ name: '', phone: '', province: '', city: '', district: '', detail: '', is_default: false });

const AddressDrawer = ({ open, onClose, userId, onSaved }: {
  open: boolean; onClose: () => void; userId: string; onSaved: (msg: string) => void;
}) => {
  const [list, setList] = useState<Address[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<Address | null>(null);
  const [form, setForm] = useState(emptyAddr());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const fetchList = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('addresses').select('*').eq('user_id', userId).order('is_default', { ascending: false }).order('created_at', { ascending: false });
    setList((data as Address[]) ?? []);
    setLoading(false);
  }, [userId]);

  useEffect(() => { if (open) { fetchList(); setEditing(null); } }, [open, fetchList]);

  const startEdit = (addr?: Address) => {
    if (addr) { setEditing(addr); setForm({ name: addr.name, phone: addr.phone, province: addr.province, city: addr.city, district: addr.district, detail: addr.detail, is_default: addr.is_default }); }
    else { setEditing({ id: '' } as Address); setForm(emptyAddr()); }
    setErr('');
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setErr('请填写收货人'); return; }
    if (!form.phone.trim()) { setErr('请填写手机号'); return; }
    if (!form.detail.trim()) { setErr('请填写详细地址'); return; }
    setSaving(true); setErr('');
    if (editing?.id) {
      await supabase.from('addresses').update({ ...form }).eq('id', editing.id);
    } else {
      await supabase.from('addresses').insert({ ...form, user_id: userId });
    }
    if (form.is_default) {
      const { data: all } = await supabase.from('addresses').select('id').eq('user_id', userId);
      const ids = (all ?? []).map((a: { id: string }) => a.id);
      if (ids.length) await supabase.from('addresses').update({ is_default: false }).in('id', ids);
      if (editing?.id) await supabase.from('addresses').update({ is_default: true }).eq('id', editing.id);
    }
    setSaving(false);
    await fetchList();
    setEditing(null);
    onSaved(editing?.id ? '地址已更新' : '地址已添加');
  };

  const handleDelete = async (id: string) => {
    await supabase.from('addresses').delete().eq('id', id);
    await fetchList();
    onSaved('地址已删除');
  };

  const handleSetDefault = async (id: string) => {
    const { data: all } = await supabase.from('addresses').select('id').eq('user_id', userId);
    const ids = (all ?? []).map((a: { id: string }) => a.id);
    if (ids.length) await supabase.from('addresses').update({ is_default: false }).in('id', ids);
    await supabase.from('addresses').update({ is_default: true }).eq('id', id);
    await fetchList();
  };

  // 编辑表单视图
  if (editing !== null) {
    return (
      <Drawer open={open} onClose={() => setEditing(null)} title={editing.id ? '编辑地址' : '新增地址'}>
        <div className="space-y-3">
          {[
            { label: '收货人', key: 'name', placeholder: '请输入姓名', maxLength: 10 },
            { label: '手机号', key: 'phone', placeholder: '请输入手机号', maxLength: 11 },
            { label: '省份', key: 'province', placeholder: '如：广东省', maxLength: 20 },
            { label: '城市', key: 'city', placeholder: '如：深圳市', maxLength: 20 },
            { label: '区/县', key: 'district', placeholder: '如：南山区', maxLength: 20 },
          ].map(f => (
            <div key={f.key}>
              <label className="text-sm font-medium text-gray-700 block mb-1">{f.label}</label>
              <input
                value={(form as Record<string, string>)[f.key]}
                onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                placeholder={f.placeholder} maxLength={f.maxLength}
                className="w-full h-11 px-4 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400 bg-gray-50"
              />
            </div>
          ))}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">详细地址</label>
            <textarea
              value={form.detail}
              onChange={e => setForm(p => ({ ...p, detail: e.target.value }))}
              placeholder="街道、楼栋、门牌号等" maxLength={100} rows={2}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400 bg-gray-50 resize-none"
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <div
              onClick={() => setForm(p => ({ ...p, is_default: !p.is_default }))}
              className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${form.is_default ? 'bg-rose-500 border-rose-500' : 'border-gray-300'}`}
            >
              {form.is_default && <i className="ri-check-line text-white text-xs" />}
            </div>
            <span className="text-sm text-gray-600">设为默认地址</span>
          </label>
          {err && <p className="text-xs text-red-500 flex items-center gap-1"><i className="ri-error-warning-line" />{err}</p>}
          <button
            onClick={handleSave} disabled={saving}
            className="w-full h-12 bg-rose-500 text-white rounded-full text-sm font-medium cursor-pointer whitespace-nowrap disabled:opacity-50 mt-1"
          >
            {saving ? '保存中...' : '保存地址'}
          </button>
        </div>
      </Drawer>
    );
  }

  // 地址列表视图
  return (
    <Drawer open={open} onClose={onClose} title="地址管理">
      {loading ? (
        <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="space-y-3">
          {list.length === 0 && (
            <div className="text-center py-8 text-gray-400">
              <i className="ri-map-pin-line text-4xl block mb-2" />
              <p className="text-sm">暂无收货地址</p>
            </div>
          )}
          {list.map(addr => (
            <div key={addr.id} className="border border-gray-100 rounded-2xl p-4 bg-gray-50/60">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-gray-900 text-sm">{addr.name}</span>
                    <span className="text-gray-500 text-sm">{addr.phone}</span>
                    {addr.is_default && <span className="text-xs bg-rose-500 text-white px-2 py-0.5 rounded-full">默认</span>}
                  </div>
                  <p className="text-xs text-gray-500 leading-relaxed">{addr.province}{addr.city}{addr.district}{addr.detail}</p>
                </div>
                <button onClick={() => startEdit(addr)} className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-gray-400 cursor-pointer">
                  <i className="ri-edit-line text-lg" />
                </button>
              </div>
              <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-100">
                {!addr.is_default && (
                  <button onClick={() => handleSetDefault(addr.id)} className="text-xs text-gray-500 cursor-pointer whitespace-nowrap">
                    设为默认
                  </button>
                )}
                <button onClick={() => handleDelete(addr.id)} className="text-xs text-red-400 cursor-pointer whitespace-nowrap ml-auto">
                  删除
                </button>
              </div>
            </div>
          ))}
          <button
            onClick={() => startEdit()}
            className="w-full h-12 border-2 border-dashed border-gray-200 rounded-2xl text-sm text-gray-400 flex items-center justify-center gap-2 cursor-pointer"
          >
            <i className="ri-add-line" /> 新增地址
          </button>
        </div>
      )}
    </Drawer>
  );
};

/* ─────────────────────────────────────────
   登录密码抽屉
───────────────────────────────────────── */
const LoginPwdDrawer = ({ open, onClose, userId, onSaved }: {
  open: boolean; onClose: () => void; userId: string; onSaved: (msg: string) => void;
}) => {
  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [show, setShow] = useState({ old: false, new: false, confirm: false });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { if (open) { setOldPwd(''); setNewPwd(''); setConfirmPwd(''); setErr(''); } }, [open]);

  const handleSave = async () => {
    if (!oldPwd) { setErr('请输入旧密码'); return; }
    if (newPwd.length < 6) { setErr('新密码至少6位'); return; }
    if (newPwd !== confirmPwd) { setErr('两次密码不一致'); return; }
    setSaving(true); setErr('');
    const { data } = await supabase.from('custom_users').select('password_hash').eq('id', userId).maybeSingle();
    if (!data) { setErr('验证失败，请重试'); setSaving(false); return; }
    const { valid: oldPwdValid, isLegacy: oldPwdLegacy } = await verifyPassword(oldPwd, data.password_hash);
    if (!oldPwdValid) { setErr('旧密码错误'); setSaving(false); return; }
    const newHash = await hashPassword(newPwd);
    const { error } = await supabase.from('custom_users').update({ password_hash: newHash }).eq('id', userId);
    // 如果旧密码是旧格式，这次修改后已经自动升级为新格式
    setSaving(false);
    if (error) { setErr('修改失败，请重试'); return; }
    onSaved('登录密码已修改');
    onClose();
  };

  const fields = [
    { label: '旧密码', val: oldPwd, set: setOldPwd, key: 'old' as const, placeholder: '请输入当前密码' },
    { label: '新密码', val: newPwd, set: setNewPwd, key: 'new' as const, placeholder: '至少6位' },
    { label: '确认新密码', val: confirmPwd, set: setConfirmPwd, key: 'confirm' as const, placeholder: '再次输入新密码' },
  ];

  return (
    <Drawer open={open} onClose={onClose} title="修改登录密码">
      <div className="space-y-4">
        {fields.map(f => (
          <div key={f.key}>
            <label className="text-sm font-medium text-gray-700 block mb-1.5">{f.label}</label>
            <div className="relative">
              <input
                type={show[f.key] ? 'text' : 'password'}
                value={f.val} onChange={e => { f.set(e.target.value); setErr(''); }}
                placeholder={f.placeholder}
                className="w-full h-12 px-4 pr-10 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400 bg-gray-50"
              />
              <button type="button" onClick={() => setShow(p => ({ ...p, [f.key]: !p[f.key] }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer">
                <i className={`${show[f.key] ? 'ri-eye-line' : 'ri-eye-off-line'} text-base`} />
              </button>
            </div>
          </div>
        ))}
        {err && <p className="text-xs text-red-500 flex items-center gap-1"><i className="ri-error-warning-line" />{err}</p>}
        <button
          onClick={handleSave} disabled={saving}
          className="w-full h-12 bg-rose-500 text-white rounded-full text-sm font-medium cursor-pointer whitespace-nowrap disabled:opacity-50 mt-2"
        >
          {saving ? '修改中...' : '确认修改'}
        </button>
      </div>
    </Drawer>
  );
};

/* ─────────────────────────────────────────
   6位数字密码输入组件（独立，避免重渲染丢失状态）
───────────────────────────────────────── */
const PinInput = ({ onComplete, label, autoFocus, clearKey }: {
  onComplete: (v: string) => void;
  label: string;
  autoFocus?: boolean;
  clearKey?: number;
}) => {
  const [value, setValue] = useState('');
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setValue('');
    onComplete('');
    if (autoFocus) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearKey]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cleaned = e.target.value.replace(/\D/g, '').slice(0, 6);
    setValue(cleaned);
    onComplete(cleaned);
  };

  const digits = Array.from({ length: 6 }, (_, i) => value[i] ?? '');
  const activeIndex = Math.min(value.length, 5);

  return (
    <div>
      <label className="text-sm font-medium text-gray-700 block mb-2">{label}</label>
      <div className="relative" onClick={() => inputRef.current?.focus()}>
        <input
          ref={inputRef}
          type="tel"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          value={value}
          onChange={handleChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className="absolute inset-0 w-full h-full opacity-0 z-10 cursor-pointer"
          style={{ fontSize: 16 }}
        />
        <div className="flex items-center justify-between gap-2">
          {digits.map((digit, index) => {
            const isActive = focused && index === activeIndex;
            const isFilled = !!digit;
            return (
              <div
                key={index}
                className={`flex-1 h-[52px] flex items-center justify-center border-2 rounded-xl transition-all duration-150
                  ${isActive ? 'border-rose-400 bg-white' : isFilled ? 'border-rose-300 bg-rose-50' : 'border-gray-200 bg-gray-50'}`}
              >
                {isFilled ? (
                  <div className="w-3 h-3 rounded-full bg-rose-500" />
                ) : isActive ? (
                  <div className="w-0.5 h-6 bg-rose-400 animate-pulse" />
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   支付密码抽屉（6位数字 + 邮箱验证）
───────────────────────────────────────── */
const PayPwdDrawer = ({ open, onClose, userId, hasPayPwd, onSaved }: {
  open: boolean; onClose: () => void; userId: string; hasPayPwd: boolean; onSaved: (msg: string) => void;
}) => {
  const [step, setStep] = useState<'set' | 'done'>('set');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [pinClearKey, setPinClearKey] = useState(0);

  useEffect(() => {
    if (open) {
      setStep('set');
      setNewPwd(''); setConfirmPwd(''); setErr('');
      setPinClearKey(k => k + 1);
    }
  }, [open]);

  const handleSet = async () => {
    if (newPwd.length !== 6) { setErr('请输入6位数字密码'); return; }
    if (!/^\d{6}$/.test(newPwd)) { setErr('支付密码只能是数字'); return; }
    if (newPwd !== confirmPwd) { setErr('两次密码不一致，请重新输入'); return; }
    setSaving(true); setErr('');
    const hash = await hashPassword(newPwd);
    const { error } = await supabase.from('custom_users').update({ pay_password: hash }).eq('id', userId);
    setSaving(false);
    if (error) { setErr('设置失败，请重试'); return; }
    setStep('done');
  };

  const drawerTitle = hasPayPwd ? '修改支付密码' : '设置支付密码';

  return (
    <Drawer open={open} onClose={onClose} title={drawerTitle}>
      <div className="space-y-5">
        {step === 'set' && (
          <>
            <div className="bg-amber-50 rounded-xl px-4 py-3">
              <div className="flex items-start gap-2">
                <i className="ri-information-line text-amber-500 text-sm mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700 leading-relaxed">支付密码用于积分兑换时的身份验证，请设置一个6位数字密码，妥善保管</p>
              </div>
            </div>
            <PinInput onComplete={setNewPwd} label={hasPayPwd ? '新支付密码（6位数字）' : '设置支付密码（6位数字）'} autoFocus clearKey={pinClearKey} />
            <PinInput onComplete={setConfirmPwd} label="确认支付密码" clearKey={pinClearKey} />
            {err && <p className="text-xs text-red-500 flex items-center gap-1"><i className="ri-error-warning-line" />{err}</p>}
            <button onClick={handleSet} disabled={saving || newPwd.length !== 6 || confirmPwd.length !== 6}
              className="w-full h-12 bg-rose-500 text-white rounded-full text-sm font-medium cursor-pointer whitespace-nowrap disabled:opacity-50">
              {saving ? '设置中...' : '确认设置'}
            </button>
          </>
        )}
        {step === 'done' && (
          <div className="flex flex-col items-center py-6">
            <div className="w-20 h-20 flex items-center justify-center bg-emerald-50 rounded-full mb-4"
              style={{ animation: 'popIn 0.4s cubic-bezier(0.34,1.56,0.64,1)' }}>
              <i className="ri-check-line text-emerald-500 text-4xl" />
            </div>
            <h3 className="font-bold text-gray-900 text-base mb-2">
              {hasPayPwd ? '支付密码修改成功' : '支付密码设置成功'}
            </h3>
            <p className="text-sm text-gray-500 text-center mb-6">
              {hasPayPwd ? '新支付密码已生效，下次兑换时使用新密码' : '支付密码已设置，兑换商品时需要输入此密码'}
            </p>
            <button
              onClick={() => { onSaved(hasPayPwd ? '支付密码已修改' : '支付密码已设置'); onClose(); }}
              className="w-full h-12 bg-emerald-500 text-white rounded-full text-sm font-medium cursor-pointer whitespace-nowrap"
            >
              完成
            </button>
          </div>
        )}
      </div>
      <style>{`@keyframes popIn { from { transform: scale(0.7); opacity: 0; } to { transform: scale(1); opacity: 1; } }`}</style>
    </Drawer>
  );
};

/* ─────────────────────────────────────────
   主页面
───────────────────────────────────────── */
const SettingsPage = () => {
  const navigate = useNavigate();
  const [showLogout, setShowLogout] = useState(false);
  const { user, refresh } = useAuth();
  const { profile } = useProfile();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [toast, setToast] = useState('');

  const [drawer, setDrawer] = useState<'profile' | 'address' | 'loginPwd' | 'payPwd' | null>(null);
  const [hasPayPwd, setHasPayPwd] = useState(false);

  const displayName = profile?.nickname ?? user?.username ?? '用户';
  const displayPhone = profile?.phone || '';
  const displayAvatar = profile?.avatar_url || '';

  const avatarColors = [
    'from-rose-400 to-pink-500', 'from-orange-400 to-amber-500',
    'from-emerald-400 to-teal-500', 'from-violet-400 to-purple-500',
    'from-sky-400 to-cyan-500', 'from-red-400 to-rose-500',
    'from-indigo-400 to-blue-500', 'from-fuchsia-400 to-pink-500',
  ];
  const avatarChar = displayName.charAt(0) || '用';
  const colorIndex = displayName.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % avatarColors.length;
  const avatarGradient = avatarColors[colorIndex];

  // 检查是否已设置支付密码
  useEffect(() => {
    if (!user) return;
    supabase.from('custom_users').select('pay_password').eq('id', user.id).maybeSingle().then(({ data }) => {
      setHasPayPwd(!!data?.pay_password);
    });
  }, [user]);

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  const handleAvatarClick = () => { if (!uploading) fileInputRef.current?.click(); };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    if (!file.type.startsWith('image/')) { setUploadError('请选择图片文件'); return; }
    if (file.size > 5 * 1024 * 1024) { setUploadError('图片不能超过 5MB'); return; }
    setUploadError(''); setUploading(true);
    try {
      const ext = file.name.split('.').pop() ?? 'jpg';
      const filePath = `${user.id}/avatar_${Date.now()}.${ext}`;
      const { error: uploadErr } = await supabase.storage.from('avatars').upload(filePath, file, { upsert: true, contentType: file.type });
      if (uploadErr) throw new Error(uploadErr.message);
      const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(filePath);
      const { error: updateErr } = await supabase.from('custom_users').update({ avatar_url: urlData.publicUrl }).eq('id', user.id);
      if (updateErr) throw new Error(updateErr.message);
      await refresh();
      showToast('头像更新成功！');
    } catch (err: unknown) {
      setUploadError(err instanceof Error ? err.message : '上传失败，请重试');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleLogout = async () => { await signOut(); setShowLogout(false); navigate('/login', { replace: true }); };

  return (
    <div className="min-h-screen bg-gray-50 pb-20 max-w-[480px] mx-auto">
      <header className="sticky top-0 z-40 bg-white border-b border-gray-100">
        <div className="flex items-center px-4 h-14 gap-3">
          <button onClick={() => navigate(-1)} className="w-8 h-8 flex items-center justify-center cursor-pointer">
            <i className="ri-arrow-left-line text-gray-700 text-xl" />
          </button>
          <h1 className="font-bold text-gray-900 text-base">设置</h1>
        </div>
      </header>

      {/* Avatar card */}
      <div className="bg-white mx-4 mt-4 rounded-2xl p-4">
        <div className="flex items-center gap-4">
          <div className="relative flex-shrink-0" onClick={handleAvatarClick}>
            <div className="w-16 h-16 rounded-2xl overflow-hidden cursor-pointer">
              {displayAvatar ? (
                <img src={displayAvatar} alt="avatar" className="w-full h-full object-cover object-top" />
              ) : (
                <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br ${avatarGradient}`}>
                  <span className="text-white font-bold text-2xl leading-none">{avatarChar}</span>
                </div>
              )}
            </div>
            <div className="absolute inset-0 rounded-2xl bg-black/30 flex items-center justify-center cursor-pointer opacity-0 hover:opacity-100 transition-opacity">
              {uploading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <i className="ri-camera-line text-white text-lg" />}
            </div>
            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-rose-500 rounded-full flex items-center justify-center border-2 border-white">
              <i className="ri-camera-fill text-white" style={{ fontSize: 9 }} />
            </div>
          </div>
          <div className="flex-1">
            <p className="font-bold text-gray-900">{displayName}</p>
            <p className="text-sm text-gray-500 mt-0.5">{displayPhone || '未绑定手机号'}</p>
            <button onClick={handleAvatarClick} disabled={uploading} className="mt-2 text-xs text-rose-500 font-medium cursor-pointer whitespace-nowrap disabled:opacity-50">
              {uploading ? '上传中...' : '更换头像'}
            </button>
          </div>
          <i className="ri-arrow-right-s-line text-gray-400 text-xl" />
        </div>
        {uploadError && (
          <div className="mt-3 flex items-center gap-2 text-xs text-red-500 bg-red-50 px-3 py-2 rounded-xl">
            <i className="ri-error-warning-line flex-shrink-0" />{uploadError}
          </div>
        )}
        <p className="text-xs text-gray-400 mt-3">支持 JPG、PNG、GIF 格式，大小不超过 5MB</p>
      </div>

      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />

      {/* Settings Groups */}
      <div className="bg-white mx-4 mt-3 rounded-2xl overflow-hidden">
        <button
          onClick={() => setDrawer('profile')}
          className="w-full flex items-center gap-3 px-4 py-4 cursor-pointer active:bg-gray-50 transition-colors border-b border-gray-50"
        >
          <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-rose-50">
            <i className="ri-user-line text-rose-500 text-lg" />
          </div>
          <span className="flex-1 text-sm text-gray-800 text-left">个人信息</span>
          <i className="ri-arrow-right-s-line text-gray-400 text-xl" />
        </button>
        <button
          onClick={() => setDrawer('address')}
          className="w-full flex items-center gap-3 px-4 py-4 cursor-pointer active:bg-gray-50 transition-colors"
        >
          <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-amber-50">
            <i className="ri-map-pin-line text-amber-500 text-lg" />
          </div>
          <span className="flex-1 text-sm text-gray-800 text-left">地址管理</span>
          <i className="ri-arrow-right-s-line text-gray-400 text-xl" />
        </button>
      </div>

      <div className="bg-white mx-4 mt-3 rounded-2xl overflow-hidden">
        <button
          onClick={() => setDrawer('loginPwd')}
          className="w-full flex items-center gap-3 px-4 py-4 cursor-pointer active:bg-gray-50 transition-colors border-b border-gray-50"
        >
          <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-emerald-50">
            <i className="ri-lock-password-line text-emerald-500 text-lg" />
          </div>
          <span className="flex-1 text-sm text-gray-800 text-left">登录密码</span>
          <i className="ri-arrow-right-s-line text-gray-400 text-xl" />
        </button>
        <button
          onClick={() => setDrawer('payPwd')}
          className="w-full flex items-center gap-3 px-4 py-4 cursor-pointer active:bg-gray-50 transition-colors"
        >
          <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-violet-50">
            <i className="ri-shield-keyhole-line text-violet-500 text-lg" />
          </div>
          <span className="flex-1 text-sm text-gray-800 text-left">{hasPayPwd ? '修改支付密码' : '设置支付密码'}</span>
          <i className="ri-arrow-right-s-line text-gray-400 text-xl" />
        </button>
      </div>

      {/* Logout */}
      <div className="mx-4 mt-4">
        <button onClick={() => setShowLogout(true)} className="w-full bg-white text-rose-500 py-4 rounded-2xl text-sm font-medium cursor-pointer whitespace-nowrap border border-rose-100">
          退出登录
        </button>
      </div>

      {/* Logout Confirm */}
      {showLogout && (
        <>
          <div className="fixed inset-0 bg-black/40 z-50" onClick={() => setShowLogout(false)} />
          <div className="fixed bottom-0 left-0 right-0 max-w-[480px] mx-auto bg-white rounded-t-2xl z-50 px-5 pt-5 pb-8">
            <h3 className="font-bold text-gray-900 text-base mb-2">确认退出登录？</h3>
            <p className="text-sm text-gray-500 mb-5">退出后需要重新登录才能使用完整功能</p>
            <div className="flex gap-3">
              <button onClick={() => setShowLogout(false)} className="flex-1 border border-gray-200 text-gray-600 py-3 rounded-full text-sm cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={handleLogout} className="flex-1 bg-rose-500 text-white py-3 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap">确认退出</button>
            </div>
          </div>
        </>
      )}

      {/* 功能抽屉 */}
      <ProfileDrawer
        open={drawer === 'profile'} onClose={() => setDrawer(null)}
        nickname={displayName} phone={displayPhone}
        userId={user?.id ?? ''}
        onSaved={msg => { showToast(msg); refresh(); setDrawer(null); }}
      />
      <AddressDrawer
        open={drawer === 'address'} onClose={() => setDrawer(null)}
        userId={user?.id ?? ''}
        onSaved={msg => showToast(msg)}
      />
      <LoginPwdDrawer
        open={drawer === 'loginPwd'} onClose={() => setDrawer(null)}
        userId={user?.id ?? ''}
        onSaved={msg => { showToast(msg); setDrawer(null); }}
      />
      <PayPwdDrawer
        open={drawer === 'payPwd'} onClose={() => setDrawer(null)}
        userId={user?.id ?? ''} hasPayPwd={hasPayPwd}
        onSaved={msg => { showToast(msg); setHasPayPwd(true); setDrawer(null); }}
      />

      <Toast msg={toast} />

      <style>{`
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
      `}</style>
    </div>
  );
};

export default SettingsPage;
